// src/excel/builders/top40/buildWorkbook.js
// Формирует Excel-отчёт TOP-N аналогично PDF-шапке
export function buildTop40Workbook(XLSX, params = {}) {
  const {
    t,
    shortcode,
    stationName,
    serviceName,
    year,
    month,
    monthLabel,
    rows = [],
    info,
    missingDates = [],
    limit = 40,
  } = params;

  const workbook = XLSX.utils.book_new();
  const worksheet = {};
  const merges = [];
  const columnCount = 4;

  const stationTitle = stationName || shortcode || '';
  const monthLine = `${t ? t('top40.pick_month') : 'Month'}: ${monthLabel || `${year || ''}-${String(month || '').padStart(2, '0')}`}`;
  const madeOnLine = (t && t('pdf.made_on')) || 'Report made on Radiomonitor.net';
  const headerRows = [
    [serviceName || (t ? t('top40.title') : 'Top-40 tracks by month')],
    ...(stationTitle ? [[stationTitle]] : []),
    [monthLine],
    [madeOnLine],
    [''],
  ];

  XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: { r: 0, c: 0 } });
  headerRows.forEach((_, idx) => {
    merges.push({ s: { r: idx, c: 0 }, e: { r: idx, c: columnCount - 1 } });
  });

  const tableHeaderRow = headerRows.length;
  const tableHeader = [
    (t && t('top40.table.rank')) || '#',
    (t && t('top40.table.artist')) || 'Artist',
    (t && t('top40.table.track')) || 'Track',
    (t && t('top40.table.count')) || 'Count',
  ];
  const tableRows = rows.map((r, idx) => ([
    idx + 1,
    r?.artist || '',
    r?.track || '',
    typeof r?.count === 'number' ? r.count : Number(r?.count || 0),
  ]));
  const tableBlock = [tableHeader, ...tableRows];
  XLSX.utils.sheet_add_aoa(worksheet, tableBlock, { origin: { r: tableHeaderRow, c: 0 } });

  let footerRow = tableHeaderRow + tableBlock.length;
  const infoRows = [];
  if (info) infoRows.push([info]);
  if (Array.isArray(missingDates) && missingDates.length) {
    infoRows.push([
      `${(t && t('top40.missing_dates')) || 'Missing dates'}: ${missingDates.join(', ')}`
    ]);
  }
  if (infoRows.length) {
    XLSX.utils.sheet_add_aoa(worksheet, [[''], ...infoRows], { origin: { r: footerRow, c: 0 } });
    footerRow += infoRows.length + 1;
    infoRows.forEach((_, idx) => {
      // +1 to skip пустую строку
      merges.push({
        s: { r: tableHeaderRow + tableBlock.length + idx + 1, c: 0 },
        e: { r: tableHeaderRow + tableBlock.length + idx + 1, c: columnCount - 1 }
      });
    });
  }

  const encode = XLSX.utils.encode_cell;
  const applyStyle = (r, c, style) => {
    const addr = encode({ r, c });
    if (!worksheet[addr]) return;
    worksheet[addr].s = { ...(worksheet[addr].s || {}), ...style };
  };

  // Header styles
  applyStyle(0, 0, {
    font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  });
  if (stationTitle) {
    applyStyle(1, 0, {
      font: { bold: true, sz: 16, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    });
  }
  const monthRowIndex = stationTitle ? 2 : 1;
  applyStyle(monthRowIndex, 0, {
    font: { sz: 11, color: { rgb: '4F4F4F' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  });
  applyStyle(monthRowIndex + 1, 0, {
    font: { sz: 10, color: { rgb: '666666' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  });

  // Table header styling
  const headerStyle = {
    font: { bold: true },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };
  for (let col = 0; col < columnCount; col += 1) {
    applyStyle(tableHeaderRow, col, headerStyle);
  }

  const bodyBorder = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  const tableDataStart = tableHeaderRow + 1;
  const tableDataEnd = tableDataStart + tableRows.length;
  for (let r = tableDataStart; r < tableDataEnd; r += 1) {
    for (let c = 0; c < columnCount; c += 1) {
      const alignment = c === columnCount - 1
        ? { horizontal: 'right', vertical: 'center' }
        : { horizontal: 'left', vertical: 'center' };
      applyStyle(r, c, {
        font: { sz: 11, color: { rgb: '1F1F1F' } },
        alignment,
        ...bodyBorder
      });
      if (c === columnCount - 1) {
        const addr = encode({ r, c });
        if (worksheet[addr]) {
          worksheet[addr].z = '#,##0';
          if (typeof worksheet[addr].v === 'string') {
            const numeric = Number(worksheet[addr].v.replace(/[^0-9.-]/g, '')) || 0;
            worksheet[addr].v = numeric;
            worksheet[addr].t = 'n';
          }
        }
      }
    }
  }

  const infoStartRow = tableHeaderRow + tableBlock.length + 1;
  for (let idx = 0; idx < infoRows.length; idx += 1) {
    applyStyle(infoStartRow + idx, 0, {
      font: { sz: 10, color: { rgb: '666666' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    });
  }

  worksheet['!merges'] = merges;
  worksheet['!cols'] = [
    { wch: 8 },
    { wch: 32 },
    { wch: 42 },
    { wch: 14 }
  ];

  const sheetNameBase = `TOP-${limit || rows.length || 40}`;
  const sheetName = sheetNameBase.slice(0, 31) || 'TOP';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  return workbook;
}

export default buildTop40Workbook;
